import type { Deal, TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { dedupeById, sumUniqueBy, isAged, ageSeverity, formatAge, splitSCNames } from '../../utils/deals';
import { MetricCard } from '../ui/MetricCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { OfferingChart } from '../charts/OfferingChart';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { useDeals } from '../../context/DealsContext';
import React from 'react';
interface Props {
  state: TabState;
  searchRows?: React.ReactNode;
}
function regionBadge(region: string) {
  const styles: Record<string, {
    bg: string;
    color: string;
  }> = {
    AMER: {
      bg: '#EFF6FF',
      color: '#1D4ED8'
    },
    EMEA: {
      bg: '#F5F3FF',
      color: '#7C3AED'
    },
    JAPAC: {
      bg: '#F0FDF4',
      color: '#15803D'
    }
  };
  const s = styles[region] || {
    bg: '#F9FAFB',
    color: '#374151'
  };
  return <span className="badge" style={{
    background: s.bg,
    color: s.color,
    fontSize: 8
  }} data-id="element-415" data-mp="16xju2e10dtg8">{region}</span>;
}
export function SDPanel({
  state,
  searchRows
}: Props) {
  const {
    deals
  } = useDeals();
  const all = getFiltered('sd', state, deals);
  const ip = all.filter(d => d.status === 'In Progress');
  const aged = ip.filter(d => isAged(d));
  const scs = new Set(ip.flatMap(d => splitSCNames(d.sc))).size;
  const oppGnarr = sumUniqueBy(all, d => d.opp_gnarr);

  // Closed Booked GNARR: won deals matching region/sub and close_q quarter filter
  const wonDeals = dedupeById(deals.filter(d => {
    if (d.adjusted_commit !== 'Won') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (state.q !== 'All' || state.fy !== 'All') {
      if (!d.close_q) return false;
      const [yr, qt] = d.close_q.split('-');
      if (state.fy !== 'All' && yr !== state.fy) return false;
      if (state.q !== 'All' && qt !== state.q) return false;
    }
    return true;
  }));
  const wonGnarr = wonDeals.reduce((s, d) => s + (d.opp_gnarr || 0), 0);
  const wonCount = wonDeals.length;
  const agedTop8 = aged.slice(0, 8);
  const agedColumns: SortableColumn<Deal>[] = [{
    key: 'account',
    label: 'Account',
    width: '30%',
    sortValue: d => d.account,
    render: d => <span title={d.account} data-id="element-416" data-mp="2ug1kgprhrt8r">{d.account.substring(0, 22)}</span>
  }, {
    key: 'sc',
    label: 'SC',
    width: '20%',
    sortValue: d => d.sc,
    render: d => <span title={d.sc} data-id="element-417" data-mp="2zdl301v82s6i">{d.sc.split(' ')[0]}</span>
  }, {
    key: 'age',
    label: 'Age',
    width: '12%',
    sortValue: d => d.age ?? 0,
    render: d => <span className="c-red" style={{
      fontWeight: 600
    }} data-id="element-418" data-mp="34c4ok15wm1yc">{formatAge(d.age)}</span>
  }, {
    key: 'gnarr',
    label: 'GNARR',
    width: '18%',
    sortValue: d => d.gnarr,
    render: d => <>{fmtG(d.gnarr)}</>
  }, {
    key: 'close_q',
    label: 'Close Q',
    width: '12%',
    sortValue: d => d.close_q,
    render: d => <>{d.close_q}</>
  }, {
    key: 'sub_region',
    label: 'Sub',
    width: '8%',
    sortValue: d => d.sub_region,
    render: d => <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-419" data-mp="3sb5e2l1kiu71">{d.sub_region}</span>
  }];
  const ipColumns: SortableColumn<Deal>[] = [{
    key: 'id',
    label: 'DR ID',
    width: '11%',
    sortValue: d => d.id,
    render: d => <span style={{
      fontSize: 9,
      color: '#8892a0'
    }} data-id="element-420" data-mp="3ei5wmdi86pwu">{d.id}</span>
  }, {
    key: 'account',
    label: 'Account',
    width: '22%',
    sortValue: d => d.account,
    render: d => <span title={d.account} data-id="element-421" data-mp="f1zj2ow9usay">{d.account.substring(0, 24)}</span>
  }, {
    key: 'sc',
    label: 'SC Assigned',
    width: '15%',
    sortValue: d => d.sc,
    render: d => <span title={d.sc} data-id="element-422" data-mp="61kvfivu514a">{d.sc.split(' ').slice(0, 2).join(' ')}</span>
  }, {
    key: 'offering',
    label: 'Offering',
    width: '14%',
    sortValue: d => d.offering,
    render: d => <span className="badge b-blue" style={{
      fontSize: 8
    }} data-id="element-423" data-mp="15dllf0bj5h2">
        {d.offering.replace('Engagement', 'Eng.').replace('Support', 'Sup.')}
      </span>
  }, {
    key: 'age',
    label: 'Age',
    width: '7%',
    sortValue: d => d.age ?? 0,
    render: d => <span className={ageSeverity(d.age) === 'high' ? 'c-red' : ageSeverity(d.age) === 'medium' ? 'c-gold' : ''} style={{
      fontWeight: ageSeverity(d.age) === 'high' ? 600 : 400
    }} data-id="element-424" data-mp="djhdhermvrn5">
        {formatAge(d.age)}
      </span>
  }, {
    key: 'gnarr',
    label: 'GNARR',
    width: '10%',
    sortValue: d => d.gnarr,
    render: d => <>{fmtG(d.gnarr)}</>
  }, {
    key: 'close_q',
    label: 'Close Q',
    width: '9%',
    sortValue: d => d.close_q,
    render: d => <>{d.close_q}</>
  }, {
    key: 'region',
    label: 'Region',
    width: '7%',
    sortValue: d => d.region,
    render: d => regionBadge(d.region)
  }, {
    key: 'sub_region',
    label: 'Sub',
    width: '5%',
    sortValue: d => d.sub_region,
    render: d => <span className="badge b-gray" style={{
      fontSize: 8
    }} data-id="element-425" data-mp="s9g857jzckxv">{d.sub_region}</span>
  }];
  const searchTable = (cols: SortableColumn<Deal>[], body: React.ReactNode) => <table className="tbl" data-id="element-426" data-mp="37z9jrnjt8jc2">
      <thead data-id="element-427" data-mp="3czntrb4z23cq">
        <tr data-id="element-428" data-mp="3d06ytr9iwx9c">
          {cols.map(col => <th key={col.key} style={{
          width: col.width
        }} data-id="element-429" data-mp="3d18mcv7q7c7y">{col.label}</th>)}
        </tr>
      </thead>
      <tbody data-id="element-430" data-mp="35i95aw7qlwrr">{body}</tbody>
    </table>;
  return <div data-id="element-431" data-mp="30huuj6er48q1">
      {/* Metrics */}
      <div className="mrow" data-id="element-432" data-mp="30gs5l40zogcr">
        <MetricCard label="In-progress requests" value={ip.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-433" data-mp="3egujkf66epmq" /> 
        <MetricCard label="Aged >90 days" value={aged.length} sub="need immediate action" colorClass="c-red" data-id="element-434" data-mp="39hzu4udpe2tn" />
        <MetricCard label="Closed Booked GNARR" value={fmtG(wonGnarr)} sub={`${wonCount} won opportunit${wonCount === 1 ? 'y' : 'ies'}`} colorClass="c-purple" data-id="element-435" data-mp="2lrdv1slofsvs" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-436" data-mp="2hoqxrc68lfr7" />
      </div>

      {/* Offering chart + Aged deals */}
      <div className="row2" data-id="element-437" data-mp="2qricplybmtnl">
        <div className="card" data-id="element-438" data-mp="2qq785jx1xhvf">
          <h3 data-id="element-439" data-mp="2qp5lsjal58ye">Requests by solution</h3>
          <OfferingChart deals={all} data-id="element-440" data-mp="2315wlnsx9qlx" />
        </div>
        <div className="card" data-id="element-441" data-mp="22zuudsf4kqwx">
          <h3 data-id="element-442" data-mp="1y2kufiu40wuu">Aged deals (&gt;90 days) at risk</h3>
          {agedTop8.length > 0 ? <SortableTable columns={agedColumns} data={agedTop8} rowKey={d => `${d.id}`} defaultSort={{
          key: 'age',
          dir: 'desc'
        }} data-id="element-443" data-mp="1y0qj8w8n4j3y" /> : <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#8892a0',
          fontSize: 11
        }} data-id="element-444" data-mp="bphu73ungf3y">No aged deals in this filter ✓</div>}
        </div>
      </div>

      {/* In progress deals list */}
      <SectionHeader title="In-progress deals — full list" data-id="element-445" data-mp="3f6hw1mxyjtj9" />
      <div className="tw" data-id="element-446" data-mp="3f82rrfw0diip">
        {searchRows ? searchTable(ipColumns, searchRows) : <SortableTable columns={ipColumns} data={ip} rowKey={d => d.id} data-id="element-447" data-mp="3k5b9jfd8vkjf" />}
      </div>

      {/* Full deals list */}
      <SectionHeader title="Closed requests — full list" data-id="element-448" data-mp="3t5raoi6imfl4" />
      <div className="tw" data-id="element-449" data-mp="3t7ataffn92q2">
        {searchRows ? searchTable(ipColumns, searchRows) : <SortableTable columns={ipColumns} data={all} rowKey={d => d.id} data-id="element-450" data-mp="14d5ba8wponlu" />}
      </div>

    </div>;
}